import React, { useEffect, useState, useCallback, useContext } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BsTruck,
  BsBoxSeam,
  BsBox,
  BsArrowRight,
  BsClockHistory,
  BsChatDots
} from "react-icons/bs";
import { SERVER_PATH } from "../../config/env";
import { UserContext } from "../../context/userContext";
import "./tracker.css";

export default function Tracker() {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);

  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchUserId, setSearchUserId] = useState("");

  // Fetch tracking data for current logged in user or searched user ID
  const fetchTracking = useCallback((userIdToFetch) => {
    if (!userIdToFetch) {
      setTrackingInfo(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    axios
      .get(`${SERVER_PATH}get/tracking.php`, {
        params: { user_id: userIdToFetch }
      })
      .then((res) => {
        if (res.data && res.data.status === "success" && res.data.tracking) {
          setTrackingInfo(res.data.tracking);
        } else {
          setTrackingInfo(null);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log("Error fetching tracking info:", err);
        setTrackingInfo(null);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    document.title = "Track Your Order - VEBBOX";
    window.scrollTo(0, 0);

    let currentId = userData?.user_id || userData?.email;
    if (!currentId) currentId = localStorage.getItem("uid");
    if (!currentId) currentId = localStorage.getItem("user_id");
    if (!currentId) {
      try {
        const parsed = JSON.parse(localStorage.getItem("userInfo"));
        if (parsed) {
          currentId = parsed.user_id || parsed.email || parsed.sub;
        }
      } catch (e) {}
    }
    if (!currentId) currentId = localStorage.getItem("mobile_no");

    if (currentId) {
      setSearchUserId(currentId);
      fetchTracking(currentId);
    } else {
      setLoading(false);
    }
  }, [userData, fetchTracking]);

  return (
    <div className="tracker-container pb-5">
      {/* HEADER BANNER */}
      <div className="tracker-header-banner mb-5">
        <Container>
          <h1 className="tracker-title">TRACK YOUR ORDER</h1>
          <p className="text-light opacity-90 fs-5 mb-0 max-w-600 mx-auto">
            Check live shipment status, courier tracking, and estimated delivery progress for your VEBBOX orders.
          </p>
        </Container>
      </div>

      <Container>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading shipment tracking...</span>
            </div>
            <p className="text-muted mt-3 fw-semibold">Fetching your shipment tracking details...</p>
          </div>
        ) : trackingInfo ? (
          /* TRACKING DETAILS CARD */
          <Row className="justify-content-center">
            <Col lg={10}>
              <Card className="tracking-card p-4 p-md-5 mb-4">
                {/* Header Row */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pb-4 border-bottom">
                  <div>
                    <h4 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                      <BsTruck className="text-success" /> Order Tracking Details
                    </h4>
                  </div>

                  {/* Track Shipment Button */}
                  {trackingInfo.tracking_url && (
                    <a
                      href={trackingInfo.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-track-shipment d-inline-flex align-items-center gap-2"
                    >
                      <BsTruck className="fs-5" /> Track Shipment <BsArrowRight />
                    </a>
                  )}
                </div>

                {/* Tracking Details Grid */}
                <Row className="g-4 my-3 py-2">
                  <Col sm={6} md={3}>
                    <div className="p-3 bg-light rounded-3">
                      <small className="text-muted text-uppercase fw-bold">Courier Company</small>
                      <h6 className="fw-bold text-dark mt-1 mb-0">
                        {trackingInfo.courier_name || "VEBBOX Express"}
                      </h6>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="p-3 bg-light rounded-3">
                      <small className="text-muted text-uppercase fw-bold">Tracking ID</small>
                      <h6 className="fw-bold text-dark mt-1 mb-0">
                        {trackingInfo.tracking_id || "N/A"}
                      </h6>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="p-3 bg-light rounded-3">
                      <small className="text-muted text-uppercase fw-bold">Last Updated</small>
                      <h6 className="fw-bold text-dark mt-1 mb-0 d-flex align-items-center gap-1">
                        <BsClockHistory className="text-success" />
                        {new Date(trackingInfo.updated_at || trackingInfo.created_at).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "long",
                          year: "numeric"
                        })}
                      </h6>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="p-3 bg-light rounded-3">
                      <small className="text-muted text-uppercase fw-bold">User / Order ID</small>
                      <h6 className="fw-bold text-dark mt-1 mb-0">
                        {trackingInfo.user_id}
                      </h6>
                    </div>
                  </Col>
                </Row>

                {/* Remarks Alert */}
                {trackingInfo.remarks && (
                  <div className="alert alert-info border-0 rounded-3 mb-0 d-flex align-items-center gap-2">
                    <BsChatDots className="fs-5" />
                    <div>
                      <strong>Note: </strong> {trackingInfo.remarks}
                    </div>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        ) : (
          /* EMPTY STATE CARD */
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="empty-tracker-card text-center p-5">
                <div className="mb-4">
                  <div className="bg-success bg-opacity-10 d-inline-flex p-4 rounded-circle text-success">
                    <BsBoxSeam className="display-3" />
                  </div>
                </div>
                <h3 className="fw-bold text-dark mb-2">Tracking Information Not Available</h3>
                <p className="text-secondary max-w-500 mx-auto mb-4" style={{ maxWidth: "550px" }}>
                  Your order has not yet been assigned a tracking number or no active dispatch details were found for ID: <strong>{searchUserId || "your account"}</strong>.
                  <br />
                  Our team will update the shipment details once your package has been dispatched. Please check back later.
                </p>
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <Button
                    variant="success"
                    className="rounded-pill px-4 py-2 fw-semibold"
                    onClick={() => navigate("/")}
                  >
                    <BsBox className="me-1" /> Continue Shopping
                  </Button>
                  <a
                    href="tel:9876543210"
                    className="btn btn-outline-secondary rounded-pill px-4 py-2 fw-semibold"
                  >
                    Contact Support
                  </a>
                </div>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}
